import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Box,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import styled from 'styled-components';
import { HighlightableRow } from 'toolkit/tables/highlightable-row';
import { useNavigateToMemberPage } from '../../app/routing/navigation-hooks';
import { NeedDto } from '@act/shared/data-transfer-objects';
import { displayName } from './utilities/displayNameAndBirthdayCheck.utils';
import { metaSliceSelectors } from 'redux/slices/metaSlice';
import useAddActionDialog from 'react-components/dialog-forms/hooks/use-add-action-dialog';
import { useTracking } from 'react-components/tracking';

interface NeedWithMember extends NeedDto {
  member?: any;
}

interface NeedRowProps {
  data?: NeedWithMember;
  onExtendTimeWindow?: (needId: number) => void;
}

const DivFitContent = styled.div`
  width: fit-content;
`;

const NeedTableRow = ({ data, onExtendTimeWindow }: NeedRowProps) => {
  const toMemberPage = useNavigateToMemberPage();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const needIssueOptions = metaSliceSelectors.useSelectNeedIssueOptions();
  const openAddActionDialog = useAddActionDialog();
  const { generateTrackingProps } = useTracking();

  if (!data) {
    return <TableRow></TableRow>;
  }

  const getTaskText = (need: NeedDto) => {
    const tenDaysFromNow = Date.now() + 10 * 24 * 60 * 60 * 1000;
    if (need.expiresAt && need.expiresAt <= tenDaysFromNow) {
      return 'Expiring Soon';
    } else {
      return 'Needs Action';
    }
  };

  const menuItems = [
    {
      display: 'View need details',
      callback: () =>
        toMemberPage(
          data.member?.assignedGuideId,
          data.memberId,
          undefined,
          data.id.toString()
        ),
      trackingName: 'view-need-details-docket-button',
    },
    {
      display: 'Add action',
      callback: () => {
        openAddActionDialog({ need: data });
      },
      trackingName: 'add-need-action-docket-button',
    },
  ];
  if (getTaskText(data) === 'Expiring Soon') {
    menuItems.push({
      display: 'Extend time window',
      callback: () => {
        onExtendTimeWindow?.(data?.id);
      },
      trackingName: 'extend-need-time-window-docket-button',
    });
  }

  const getIssueText = (need: NeedDto) => {
    const issue = needIssueOptions.find(issue => issue.value === need.issueId);
    return issue?.label;
  };

  const formatDueDate = (timestamp: number) => {
    if (!timestamp) return '––';
    return DateTime.fromMillis(timestamp).toFormat('LLL dd, yyyy');
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <HighlightableRow
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          toMemberPage(
            data.member?.assignedGuideId,
            data.memberId,
            undefined,
            data.id.toString()
          );
        }}
        {...generateTrackingProps('view-need-button-from-docket-table-row')}
      >
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName(data?.member)}
          </Box>
        </TableCell>
        <TableCell>{getIssueText(data) || '––'}</TableCell>
        <TableCell>
          <DivFitContent>{getTaskText(data)}</DivFitContent>
        </TableCell>
        <TableCell>{formatDueDate(data.expiresAt)}</TableCell>
        <TableCell>
          <IconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenMenu(e);
            }}
            {...generateTrackingProps('view-need-button-from-docket-table-row')}
          >
            <MoreVertIcon />
          </IconButton>
        </TableCell>
        <TableCell>
          <IconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              toMemberPage(
                data.member?.assignedGuideId,
                data.memberId,
                undefined,
                data.id.toString()
              );
            }}
            {...generateTrackingProps('view-need-button-from-docket-table-row')}
          >
            <ChevronRightIcon />
          </IconButton>
        </TableCell>
      </HighlightableRow>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuItems.map(item => (
          <MenuItem
            key={item.display}
            onClick={() => {
              item.callback();
              handleCloseMenu();
            }}
            {...generateTrackingProps(item.trackingName)}
          >
            {item.display}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NeedTableRow;
